import {
  Button,
  Input,
  Multiselect,
  BaseModal,
  Switch,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete-icon.svg';
import DeleteScreenModal from './delete-screen-modal';
import { BrandType } from 'models/brand.model';
import { apiService } from 'utils/api';
import { toast } from 'react-toastify';
import useDashboardContext from 'contexts/dashboard.context';
import { FormDataType } from 'models/screen.model';

type Props = {
  createScreenData: FormDataType;
  isOpen: boolean;
  onClose: () => void;
  type: 'edit' | 'create';
  refreshData?: () => void;
  onEditSuccess?: (arg0: FormDataType) => void;
  onDeleteSuccess?: (arg0: string) => void;
};

const CreateScreenModal = ({
  isOpen,
  onClose,
  type,
  createScreenData,
  refreshData,
  onEditSuccess,
  onDeleteSuccess
}: Props) => {
  const [loading, setLoading] = useState(false);
  const cokitchen_id = localStorage.getItem('cokitchen');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { activeCokitchen } = useDashboardContext();
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [values, setValues] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormDataType>(createScreenData);

  const brandOptions = useMemo(() => {
    return brands.map((brand) => ({
      value: brand.id,
      text: brand.name
    }));
  }, [brands]);

  const fetchBrands = async () => {
    const payload = {
      cokitchen_id: activeCokitchen.id,
      archived: false
    };
    try {
      const res = await apiService.get<{ data: BrandType[] }>('v2/brand_management/brands', {
        params: payload,
        baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
      });
      const cokitchenBrands = res.data.data?.filter((b) => b.cokitchen_id === cokitchen_id) ?? [];
      setBrands(cokitchenBrands);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, [isOpen]);

  useEffect(() => {
    setFormData(createScreenData);
  }, [createScreenData]);

  useEffect(() => {
    setFormData({ ...formData, brand_ids: values });
  }, [values]);

  useEffect(() => {
    setValues(formData?.brand_ids);
  }, []);

  const createScreen = async () => {
    setLoading(true);
    const data = { ...formData, cokitchen_id };
    try {
      await apiService.post('kitchen/screens', {
        data,
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL
      });
      refreshData?.();
      onClose();
      toast(<ToastMessage type='success' message='Screen created successfully' />);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const editScreen = async () => {
    if (!createScreenData.id) return;

    setLoading(true);
    const data = {
      cokitchen_id,
      active: formData.active,
      brand_ids: formData.brand_ids,
      name: formData.name
    };
    try {
      await apiService.put(`kitchen/screens/${createScreenData.id ?? ''}`, {
        data,
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL
      });

      const editedScreen: any = {
        ...data,
        brands: brands.filter((item) => data.brand_ids.includes(item.id)),
        id: createScreenData.id
      };
      onEditSuccess?.(editedScreen);
      onClose();
      toast(<ToastMessage type='success' message='Screen updated successfully' />);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    type === 'create' ? await createScreen() : await editScreen();
    setValues([]);
  };
  return (
    <BaseModal
      position='right'
      isOpen={isOpen}
      onRequestClose={onClose}
      title={type === 'create' ? 'Create Screen' : 'Edit Screen'}
      footer={
        <div className='flex justify-between'>
          <Button variant='outline' color='primary' className='mr-4 h-[49px]' onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            onClick={onConfirm}
            variant='block'
            color='primary'
            disabled={formData.name === '' || formData.brand_ids.length === 0}
            className='h-[49px]'>
            {type === 'edit' ? 'Edit Screen ' : 'Create Screen'}
          </Button>
        </div>
      }>
      <Input
        placeholder='1'
        className='min-w-full text-[#A3A3A3] mb-6'
        type='number'
        value={formData.name}
        label='Name of screen'
        onChange={(e: any) => setFormData({ ...formData, name: e.target.value })}
      />

      <div className='w-full mb-8 text-black'>
        <p className='text-sm font-medium mb-[6px]'>Brands selection</p>
        <Multiselect
          options={brandOptions}
          values={values}
          onChange={setValues}
          placeholder='Select brand'
          dropdownPlaceholder='Select all or multiple brands'
        />
      </div>

      <div className='flex bg-[#F9F6F6] rounded-md border-[0.5px] border-[#EEECEC] pl-5 py-4 mb-4'>
        <Switch
          active={formData?.active}
          onChange={(e: boolean) => setFormData({ ...formData, active: e })}
          label='Active'
          small
          labelPosition='right'
        />
      </div>
      {type === 'edit' && (
        <div
          onClick={() => setShowDeleteModal(true)}
          className='text-[#E60A2B] flex gap-3 items-center font-medium cursor-pointer mb-4'>
          <DeleteIcon /> Delete screen
        </div>
      )}
      <DeleteScreenModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        data={createScreenData}
        onDelete={() => {
          onDeleteSuccess?.(createScreenData.id ?? '');
          onClose();
        }}
      />
    </BaseModal>
  );
};

export default CreateScreenModal;
